<script setup>
import { computed, ref, watchEffect, watch, onMounted } from "vue";
import RequiredInformations from "@/components/shared/RequiredInformations.vue";
import { useStore } from "vuex";
import {
  citizenEvent,
  getLoggedStatus,
  getResource,
  getServices,
  updateReservation,
  updateSlots,
} from "@/api/httpApi";
import {
  validateForm, showFormToast
} from "@/utility.js";
import * as Yup from "yup";

const emit = defineEmits(["previous", "next", "goto"]);
//const informations = ref(["Motivo", "Dettagli", "Attributi aggiuntivi"]);
const store = useStore();
const resource = ref();
const services = ref();
const selectedService = ref(store.state.serviceId || "");
const detail = ref(store.state.detail || "");
const attributes = ref(store.state.attributes || {});
const isRecapping = ref(store.state.isRecapping || undefined);
const isLoggedIn = ref(false);
const step3Form = ref();

const isServiceActive = computed(() => {
  return services.value?.length > 0;
});

const doesServiceDetermines = computed(() => {
  return isServiceActive.value && resource.value?.enableSlotDuration;
});

// le voci del menù "informazioni richieste" sono condizionate dal motivo appuntamento
const informations = computed(() => {
  return (isServiceActive.value && !doesServiceDetermines.value) ? ["Motivo", "Dettagli", "Attributi aggiuntivi"] : ["Dettagli", "Attributi aggiuntivi"];
});

onMounted(async () => {
});

watch(
  attributes,
  (newAttributes) => {
    store.commit("setAttributes", newAttributes);
  },
  { deep: true }
);

watchEffect(() => {
  (async () => {
    const data = await getLoggedStatus();
    isLoggedIn.value = data?.roles?.includes("cittadino");
  })();
});


watchEffect(async () => {
  if (store.state.resourceId && !services.value) {
    try {
      services.value = await getServices(store.state.resourceId);
    } catch (err) {
      console.error(err);
      services.value = undefined;
    }
  }
});

watchEffect(async () => {
  store.commit("setServiceId", selectedService);
  store.commit("setDetail", detail);
  store.commit("setAttributes", attributes);
});

watchEffect(async () => {
  if (
    store.state.resourceId &&
    (!resource.value || resource.value.resourceId !== store.state.resourceId)
  ) {
    try {
      resource.value = await getResource(store.state.resourceId);

      if(resource.value && Object.keys(resource.value).length > 0) {
        createStep3Form();
      }
    } catch (err) {
      console.error(err);
      resource.value = undefined;
    }
  }
});

function createStep3Form() {
  // Schema di validazione - libreria Yup
  const obj = {};

  if(resource.value.attributes && resource.value.attributes.length) {
    resource.value.attributes.forEach((item) => {
      if(item.required && item.id) {
        obj['input_' + item.id] = Yup.string().required("Campo obbligatorio");
      }
    })
  }

  step3Form.value = Yup.object().shape({...obj, ...{
      selectService: Yup.string().when([], {
        is: () => isServiceActive.value && !doesServiceDetermines.value,
        then: (schema) =>  schema.required("Campo obbligatorio"),
        otherwise: (schema) => schema.nullable()
      }),
      detail: Yup.string().max(600, "Inserire massimo 600 caratteri")
  }});
}

function updateAttribute(attributeId, value) {
  attributes.value = { ...attributes.value, [attributeId]: value };
}


async function nextStep(autoSubmit, goTo = false) {
  setTimeout(() => {
    const obj = {};

    if (attributes.value && Object.keys(attributes.value).length > 0) {
      Object.keys(attributes.value).map(function (key) {
        obj['input_' + key] = attributes.value[key];
      });
    }

    validateForm(step3Form.value, 'step3Form', {
      ...obj, ...{
        selectService: selectedService.value,
        detail: detail.value
      }
    }, () => {
      if (autoSubmit && goTo) {

        citizenEvent(
          {
            message: "Utente compila lo step dettagli",
            stepId: 3,
            stepType: "intermedio",
            resourceId: store?.state?.resourceId ?? ''
          }
        );

        emit('goto', 4);
      } else if (autoSubmit) {
        // se tutto è valido procedo allo step successivo

        citizenEvent(
          {
            message: "Utente compila lo step dettagli",
            stepId: 3,
            stepType: "intermedio",
            resourceId: store?.state?.resourceId ?? ''
          }
        );

        formValid();
      }
    }, (errors) => {
      // in presenza di errori di validazione mostro il toast di errore
      showFormToast('step3Form', errors);
    }, () => {
      // dopo la prima validazione resto in ascolto di ogni cambiamento di input per rieseguire una validazione in tempo reale
      nextStep(false);
    });
  }, 300);
}

async function formValid() {
  try {
    if (
      isServiceActive.value &&
      !doesServiceDetermines.value &&
      !store.state.isEditing
    ) {
      await updateSlots({
        erogationMode:
          resource.value.erogationMode === "mis"
            ? store.state.erogationMode
            : resource.value.erogationMode,
        reservationNumber: store.state.numberOfReservations,
        reservationSlots: store.state.slots,
        resourceId: store.state.resourceId,
        serviceId: selectedService.value,
        reservationId: store.state.reservationId,
      });
    }

    if (store.state.isEditing) {
      const slotsNumberInService = services.value?.find(
        (service) => service.serviceId === store.state.serviceId
      );

      const payload = {
        detail: store.state.detail,
        email: store.state.user.email,
        erogationMode:
          resource.value.erogationMode === "mis"
            ? store.state.erogationMode
            : resource.value.erogationMode,
        firstName: store.state.user.firstName,
        fiscalCode: store.state.user.fiscalCode,
        lastName: store.state.user.lastName,
        mobilePhone: store.state.user.mobilePhone,
        phone: store.state.user.phone,
        reservationId: store.state.reservationId,
        reservationSlots: store.state.slots,
        reservationNumber: store.state.numberOfReservations,
        resourceId: store.state.resourceId,
        service: {
          description: "",
          serviceId: store.state.serviceId,
          slotsNumber: slotsNumberInService?.slotsNumber || 0,
          visible: true,
        },
      };

      let updatedPayload;

      if (
        store.state.attributes &&
        Object.keys(store.state.attributes).length > 0
      ) {
        updatedPayload = {
          ...payload,
          attributesValues: store.state.attributes,
        };
      } else {
        updatedPayload = payload;
      }

      if(store.state?.identifyValues.length > 0) {
        updatedPayload = {
          ...updatedPayload,
          itemsBooking: store.state?.identifyValues
        };
      }

      await updateReservation(updatedPayload, isLoggedIn.value);
    }
  } catch (err) {
    console.error(err);
  } finally {
    emit("next");
  }
}
</script>

<template>
<form id="step3Form" @submit.prevent>

  <div class="container-fluid row mx-auto p-2">

    <div class="col-3 border-end pe-0 d-none d-md-block">
      <RequiredInformations :informations="informations" :progress="60" />
    </div>

    <div class="col-12 px-0 ps-md-5 pe-md-2 mb-5 pt-4 col-md-9">

      <template v-if="isServiceActive && !doesServiceDetermines">
        <div class="bg-light p-4 shadow-sm mb-5" :id="encodeURIComponent('Motivo')">
          <h2 class="h3 mb-3">{{`Motivo${!doesServiceDetermines ? "*" : ""}`}}</h2>

          <div class="select-wrapper mt-5">
            <label for="selectService">Seleziona il motivo dell'appuntamento</label>
            <select
              id="selectService"
              v-model="selectedService"
              :disabled="doesServiceDetermines"
              name="selectService"
              data-yup-label="Motivo">
              <option selected="" value="">Seleziona opzione</option>
              <option
                v-for="service in services"
                :key="service.serviceId"
                :value="service.serviceId">
                {{ service.description }}
              </option>
            </select>
          </div>
        </div>
      </template>

      <div class="bg-light p-4 shadow-sm mb-5" :id="encodeURIComponent('Dettagli')">
        <h2 class="h3 mb-3">Dettagli</h2>
        <div class="form-group mt-5 mb-0">
          <label class="active" for="detail">Aggiungi ulteriori dettagli</label>
          <input
            type="text"
            class="form-control bg-white"
            v-model="detail"
            id="detail"
            name="detail"
            data-yup-label="Dettagli"
            placeholder="Inserire massimo 600 caratteri"
            />
        </div>
      </div>

      <template v-if="resource?.attributes?.length">
        <div class="bg-light p-4 shadow-sm" :id="encodeURIComponent('Attributi aggiuntivi')">
          <h2 class="h3 mb-3">Attributi aggiuntivi</h2>
          <p class="mb-0">Aggiungi ulteriori dettagli</p>

          <template v-for="attribute in resource.attributes" :key="attribute.id">
            <template v-if="attribute.attributeType === 'STRING'">
              <div class="form-group mt-5">
                <label class="active" :for="`input_${attribute.id}`">{{
                    `${attribute.name} ${attribute.required ? "*" : ""}`
                  }}</label>
                <input
                  :id="`input_${attribute.id}`"
                  :name="`input_${attribute.id}`"
                  type="text"
                  class="form-control bg-white"
                  :placeholder="attribute.description"
                  :aria-label="attribute.name"
                  :value="attributes[attribute.id] || ''"
                  :required="attribute.required"
                  :data-yup-label="attribute.name"
                  @change="updateAttribute(attribute.id, $event.target.value)"/>
              </div>
            </template>

            <template v-if="attribute.attributeType === 'SELECT'">
              <div class="select-wrapper mt-5">
                <label :for="`input_${attribute.id}`">{{
                    `${attribute.name} ${attribute.required ? "*" : ""}`
                  }}</label>
                <select
                  :id="`input_${attribute.id}`"
                  :name="`input_${attribute.id}`"
                  :aria-label="attribute.name"
                  :value="attributes[attribute.id] || ''"
                  :required="attribute.required"
                  :data-yup-label="attribute.name"
                  @change="updateAttribute(attribute.id, $event.target.value)">
                  <option selected="" value="">Scegli un'opzione</option>
                  <option
                    v-for="permittedValue in attribute.permittedValues"
                    :key="permittedValue"
                    :value="permittedValue">
                    {{ permittedValue }}
                  </option>
                </select>
              </div>
            </template>

            <template v-if="attribute.attributeType === 'MULSEL'">
              <div class="select-wrapper mt-5">
                <label :for="`input_${attribute.id}`">{{ `${attribute.name} ${attribute.required ? "*" : ""}` }}</label>
                <select
                  :id="`input_${attribute.id}`"
                  :name="`input_${attribute.id}`"
                  @change="
                updateAttribute(
                  attribute.id,
                  Array.from($event.target.selectedOptions)
                    .map((option) => option.value)
                    .join(';')
                )"
                  :aria-label="attribute.name"
                  multiple
                  style="height: auto; max-height: 10rem"
                  :required="attribute.required"
                  :data-yup-label="attribute.name">
                  <option
                    v-for="permittedValue in attribute.permittedValues"
                    :key="permittedValue"
                    :value="permittedValue"
                    :selected="
                  (attributes[attribute.id] || []).includes(permittedValue)">
                    {{ permittedValue }}
                  </option>
                </select>
              </div>
            </template>
          </template>

        </div>
      </template>

      <div class="d-flex justify-content-between mt-4">
        <button
          type="button"
          class="btn btn-outline-primary"
          @click="
            (e) => {
              emit('previous');
            }">
          Indietro
        </button>
        <button
          v-if="isRecapping"
          type="button"
          class="btn btn-outline-primary"
          @click="nextStep(true, true)">
          Salva
        </button>
        <button
          type="button"
          class="btn btn-outline-primary"
          @click="nextStep(true)">
          Avanti
        </button>
      </div>
    </div>
  </div>
</form>
</template>
